import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BadgePercent, FileCheck, Headphones, Shield, Star } from 'lucide-react';
import BookNowButton from '../components/BookNowButton';
import PhoneCta from '../components/PhoneCta';
import CatchyHooksSection from '../components/CatchyHooksSection';
import { ExpertEdgeSection, CallMomentsSection } from '../components/CallAttractionSections';
import AboutEditorialCta from '../components/AboutEditorialCta';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchWidget from '../components/SearchWidget';
import FareDisclaimer from '../components/FareDisclaimer';
import SectionHeader from '../components/SectionHeader';
import {
  EXCLUSIVE_DEALS,
  FEATURES,
  JOURNEY_STEPS,
  NEARBY_DEALS,
  POPULAR_DESTINATIONS,
  REVIEWS,
  TOP_DESTINATIONS,
} from '../data/mockData';
import { SITE_NAME } from '../constants/site';
import { IMAGES } from '../constants/images';

const featureIcons = [Headphones, BadgePercent, FileCheck, Shield];

export default function HomePage() {
  const navigate = useNavigate();

  const goFlightSearch = (from, to) => {
    navigate(`/flight/search/flights?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  };

  return (
    <>
      <Header />

      <div className="hero-split">
        <section className="hero hero--farehutz" aria-label="Home hero">
          <div className="hero__bg" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="hero__bg-img" />
            <div className="hero__bg-overlay" />
          </div>
          <div className="container hero--farehutz__stack">
            <h1 className="hero--farehutz__title">
              Call our Flight Booking Experts for Unpublished Discounts - Save upto 30%
            </h1>
          </div>
        </section>

        <div className="hero-split__bridge">
          <div className="container hero-split__widget">
            <SearchWidget variant="hero" />
          </div>
        </div>

        <section className="section section--destinations">
          <div className="container">
            <SectionHeader
              eyebrow="Trending now"
              title="Save more on top destinations"
              subtitle="Hand-picked routes with exclusive member pricing and flexible change options."
            />
            <div className="dest-grid">
              {TOP_DESTINATIONS.map((d, i) => (
                <article key={d.id} className={`dest-card ${i === 0 ? 'dest-card--featured' : ''}`}>
                  <div className="dest-card__media">
                    <img src={d.image} alt={d.city} loading="lazy" />
                    <div className="dest-card__overlay" />
                    <span className="dest-card__tag">Best value</span>
                  </div>
                  <div className="dest-card__body">
                    <div>
                      <h3>{d.city}</h3>
                      <p className="dest-card__country">{d.country}</p>
                    </div>
                    <div className="dest-card__footer">
                      <p className="price-label">
                        from <strong>${d.price.toFixed(0)}</strong>
                      </p>
                      <button type="button" className="btn-icon" onClick={() => goFlightSearch('New York (JFK)', d.city)} aria-label={`View fares to ${d.city}`}>
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <FareDisclaimer className="fare-disclaimer--section" />
            <PhoneCta
              variant="compact"
              title="Need help picking a route?"
              subtitle="Call our flight team — available 24/7."
            />
          </div>
        </section>
      </div>

      <CatchyHooksSection />

      <section className="section section--premium-dark">
        <div className="container">
          <SectionHeader
            eyebrow="What makes us different"
            title={`Why travelers choose ${SITE_NAME}`}
            subtitle="Simple booking, round-the-clock support, and fares you can trust."
            light
          />
          <div className="features-premium">
            {FEATURES.map((f, i) => {
              const Icon = featureIcons[i];
              return (
                <article key={f.title} className="feature-premium">
                  <div className="feature-premium__icon">
                    <Icon size={26} strokeWidth={1.75} />
                  </div>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </article>
              );
            })}
          </div>
          <PhoneCta
            variant="dark"
            title="Questions about booking?"
            subtitle="Speak with a travel specialist anytime."
          />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader
            eyebrow="Exclusive deals"
            title={`Exclusive ${SITE_NAME} flight deals for you`}
            subtitle="Limited-time fares to travel smarter and save more."
          />
          <div className="deals-grid">
            {EXCLUSIVE_DEALS.map((deal) => (
              <article key={deal.id} className="deal-card-premium">
                <div className="deal-card-premium__img">
                  <img src={deal.image} alt={`${deal.from} to ${deal.to}`} loading="lazy" />
                </div>
                <div className="deal-card-premium__body">
                  <div className="deal-card-premium__route">
                    <span>{deal.from}</span>
                    <ArrowRight size={16} aria-hidden />
                    <span>{deal.to}</span>
                  </div>
                  <p className="deal-meta">
                    {deal.type} · {deal.days} {deal.days === 1 ? 'day' : 'days'}
                  </p>
                  <p className="deal-dates">{deal.dates}</p>
                  <div className="deal-card-premium__foot">
                    <p className="price-highlight">${deal.price.toFixed(2)}</p>
                    <BookNowButton
                      label="Book now"
                      serviceType="flight"
                      bookingPayload={{
                        subject: `${SITE_NAME} — Homepage deal booking`,
                        bookingLines: [
                          { label: 'Route', value: `${deal.from} → ${deal.to}` },
                          { label: 'Type', value: deal.type },
                          { label: 'Travel dates', value: deal.dates },
                          { label: 'Duration', value: `${deal.days} day(s)` },
                          { label: 'Price', value: `$${deal.price.toFixed(2)}` },
                        ],
                        offer: {
                          providerName: `${deal.from} → ${deal.to}`,
                          fromCode: deal.from?.slice(0, 3).toUpperCase(),
                          toCode: deal.to?.slice(0, 3).toUpperCase(),
                          fromTime: '—',
                          toTime: '—',
                          dateLabel: deal.dates,
                          duration: `${deal.days} day(s)`,
                          stops: deal.type,
                          class: 'Economy',
                        },
                        price: {
                          base: deal.price,
                          total: deal.price,
                          travelerLabel: '1 Traveler(s) - Adult',
                        },
                      }}
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
          <FareDisclaimer className="fare-disclaimer--section" />
          <PhoneCta
            variant="compact"
            title="Lock in an exclusive flight deal"
            subtitle="Call now for today's best fares."
          />
        </div>
      </section>

      <section className="section section--soft">
        <div className="container">
          <SectionHeader
            eyebrow="Near you"
            title="Flight deals departing near you"
            subtitle="Based on popular routes—fares refreshed regularly."
          />
          <div className="nearby-grid">
            {NEARBY_DEALS.map((d) => (
              <article key={d.city} className="nearby-premium">
                <img src={d.image} alt={d.city} loading="lazy" />
                <div className="nearby-premium__content">
                  <h3>{d.city}</h3>
                  <p className="nearby-premium__month">{d.month}</p>
                  <p className="price-highlight">from ${d.price}</p>
                  <button type="button" className="btn-outline-light" onClick={() => goFlightSearch('Chicago (ORD)', d.city)}>
                    Explore fares
                  </button>
                </div>
              </article>
            ))}
          </div>
          <FareDisclaimer className="fare-disclaimer--section" />
          <PhoneCta
            variant="compact"
            title="Explore fares near you"
            subtitle="We compare airlines and routes for your dates."
          />
        </div>
      </section>

      <section className="section section--reviews">
        <div className="container">
          <SectionHeader
            title="Loved by travelers worldwide"
            subtitle="Real reviews from verified bookings — no incentives, no filters."
          />
          <div className="reviews-grid">
            {REVIEWS.map((r) => (
              <article key={r.title} className="review-premium">
                <div className="stars small">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={14} fill="currentColor" />
                  ))}
                </div>
                <h3>&ldquo;{r.title}&rdquo;</h3>
                <p>{r.text}</p>
              </article>
            ))}
          </div>
          <PhoneCta
            variant="compact"
            title="Join thousands of happy travelers"
            subtitle="Call us to book your next trip with confidence."
          />
        </div>
      </section>

      <ExpertEdgeSection />

      <section className="section section--popular">
        <div className="container">
          <SectionHeader
            eyebrow="Inspiration"
            title="Popular destinations"
            subtitle="Cities our members book most this season — explore hotels and packages in one click."
          />
          <div className="popular-mosaic">
            {POPULAR_DESTINATIONS.map((d, i) => (
              <Link
                key={d.city}
                to={`/flight/search/hotels?destination=${encodeURIComponent(d.city)}`}
                className={`popular-tile popular-tile--${i + 1}`}
              >
                <img src={d.image} alt={d.city} loading="lazy" />
                <div className="popular-tile__shade" />
                <div className="popular-tile__content">
                  <span className="popular-tile__city">{d.city}</span>
                  {d.country && <span className="popular-tile__country">{d.country}</span>}
                </div>
                <ArrowRight size={18} className="popular-tile__arrow" aria-hidden />
              </Link>
            ))}
          </div>
          <PhoneCta
            variant="compact"
            title="Not sure which destination to choose?"
            subtitle="Our team can recommend the best city for your dates."
          />
        </div>
      </section>

      <section className="section section--editorial">
        <div className="container">
          <div className="editorial-split">
            <SectionHeader
              eyebrow="About us"
              title={`Find cheap flights & travel deals at ${SITE_NAME}`}
              subtitle="Compare fares across airlines and hotels with support at every step."
            />
            <div className="editorial-copy">
              <p>
                Looking for affordable flights to your next destination? At {SITE_NAME}, we help you compare airline
                tickets, hotels, cars, packages, and cruises in one place. Our team is available 24/7 for booking
                assistance, changes, and questions about your itinerary.
              </p>
            </div>
          </div>
          <AboutEditorialCta />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader
            eyebrow="Your journey"
            title="We're with you every step of the way"
            subtitle="Before you book, after you book, and whenever you need help."
          />
          <div className="journey-premium">
            {JOURNEY_STEPS.map((step) => (
              <article key={step.title} className="journey-premium__card">
                <img src={step.image} alt={step.alt} loading="lazy" />
                <div className="journey-premium__overlay">
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                  <Link to="/flight/support" className="journey-link">
                    Learn more <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <PhoneCta
            variant="compact"
            title="Need help before or after you book?"
            subtitle="One phone call — we handle the rest."
          />
        </div>
      </section>

      <CallMomentsSection />

      <Footer />
    </>
  );
}
